import { consumerApiUrls } from '@/constants';
import { messageApi } from '@/context/MessageContext';
import { getApi } from '@/views';
import { PriceConfigResDto } from '@/views/UserCenter/pages/Wallet/type';
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  GenericAbortSignal,
} from 'axios';
import { ApiErrorResponse, REQUEST_CODE_HANDLE } from './handlers';
import { compatibleData } from './handlers/common';
import { getStoredUserName, logoutAndRedirect } from './auth';
import { Local } from './storage';
import trackingSDK from './tracking';

// 基础响应数据类型
interface BaseResponse<T = any> {
  code: number;
  msg?: string;
  message?: string;
  data?: T;
  fail?: boolean;
  [key: string]: any;
}

// 扩展的请求配置
export interface RequestConfig extends AxiosRequestConfig {
  silent?: boolean; // 是否静默处理错误（不显示 toast）
  unLogError?: boolean; // 是否不记录错误日志
  sourceData?: boolean; // 原始数据
  otherApi?: string; // 其他api地址
  requestId?: string; // 请求ID，用于取消请求
  cancelToken?: any; // 兼容旧版
  [key: string]: any;
}

// 扩展的 Axios 请求配置（包含拦截器使用的内部配置）
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  silent?: boolean;
  unLogError?: boolean;
  sourceData?: boolean; // 原始数据
  otherApi?: string; // 其他api地址
  requestId?: string; // 请求ID
  signal?: AbortSignal | GenericAbortSignal; // 中断信号
}

// 错误响应类型
interface ErrorResponse {
  status?: number;
  error?: string;
  data?: {
    msg?: string;
    message?: string;
    title?: string;
    error?: string;
    [key: string]: any;
  };
}

// baseURL
const envBaseURL: string = import.meta.env.VITE_API_BASE;
const baseURL: string = import.meta.env.DEV && envBaseURL ? envBaseURL : Local.get('baseURL') || envBaseURL;
// 数字金服API
const glodUrl: string = import.meta.env.VITE_API_GLOD_URL;

/**
 * 请求管理器 - 管理所有请求的中断
 */
class RequestManager {
  private static instance: RequestManager;
  static getInstance(): RequestManager {
    if (!RequestManager.instance) {
      RequestManager.instance = new RequestManager();
    }
    return RequestManager.instance;
  }

  private readonly controllers: Map<string, AbortController> = new Map();
  private requestCounter = 0;

  /**
   * 生成请求ID
   */
  generateRequestId(prefix = 'req'): string {
    return `${prefix}_${Date.now()}_${++this.requestCounter}`;
  }

  /**
   * 添加请求控制器
   */
  addController(requestId: string, controller: AbortController): void {
    // 如果已存在相同ID的请求，先取消
    if (this.controllers.has(requestId)) {
      this.controllers.get(requestId)?.abort('取消前一个相同请求');
      this.controllers.delete(requestId);
    }
    this.controllers.set(requestId, controller);
  }

  /**
   * 移除请求控制器
   */
  removeController(requestId: string): void {
    this.controllers.delete(requestId);
  }

  /**
   * 取消特定请求
   */
  cancelRequest(requestId: string, reason = '请求被取消'): boolean {
    const controller = this.controllers.get(requestId);
    if (controller) {
      controller.abort(reason);
      this.controllers.delete(requestId);
      return true;
    }
    return false;
  }

  /**
   * 取消所有请求
   */
  cancelAllRequests(reason = '取消所有请求'): void {
    this.controllers.forEach((controller) => {
      controller.abort(reason);
    });
    this.controllers.clear();
  }

  /**
   * 获取当前请求数量
   */
  getRequestCount(): number {
    return this.controllers.size;
  }
}

// 全局请求管理器
export const requestManager = RequestManager.getInstance();

/**
 * 设置请求头
 * @param headers - 请求头配置
 * @returns 合并后的请求头
 */
function appendHeaders(
  headers: Record<string, any> = {},
  authorizationToken?: string,
): Record<string, any> {
  /** 合并默认请求头 */
  function withDefaultHeaders(innerHeaders: Record<string, any>): Record<string, any> {
    const token = authorizationToken || Local.get('ny_pc_token');
    const defaultHeaders: Record<string, any> = {
      'Content-Type': 'application/json;charset=UTF-8',
    };
    if (token) {
      defaultHeaders.Authorization = token;
    }
    const userName = getStoredUserName();
    if (userName) {
      defaultHeaders['X-User-Nickname'] = encodeURIComponent(userName);
    }
    return { ...defaultHeaders, ...innerHeaders };
  }
  return withDefaultHeaders(headers);
}

// 创建 axios 实例
const service = axios.create({
  baseURL,
  headers: {},
});

// 重新登录弹窗状态

// 请求拦截器
service.interceptors.request.use(
  async (config: CustomAxiosRequestConfig) => {
    const configData = { ...config };
    const productCenterBaseUrls = [
      import.meta.env.VITE_PRODUCT_CENTER_URL,
      import.meta.env.VITE_PRODUCT_CENTER_TEMPLATE_URL,
      import.meta.env.VITE_API_URL_2,
    ].filter(Boolean);
    const useCapitalToken = productCenterBaseUrls.includes(configData.baseURL);
    const authorizationToken = useCapitalToken ? Local.get('capitalToken') : undefined;
    configData!.headers = appendHeaders(configData!.headers, authorizationToken) as any;

    // URL 处理：如果是简写形式则通过 getApi 转换
    if (/^\w{1,}\.\w{1,}$/g.test(configData!.url || '')) {
      configData!.url = getApi(configData!.url!);
    }
    if (Object.prototype.toString.call(configData!.data) === '[object Array]') {
      configData!.url += `/${configData!.data.join('/')}`;
    }

    // 生成请求ID
    const requestId = configData!.requestId || requestManager.generateRequestId();
    configData!.requestId = requestId;

    // 创建 AbortController
    const controller = new AbortController();
    requestManager.addController(requestId, controller);

    // 设置中断信号
    configData!.signal = controller.signal;

    // 过滤请求接口
    const { url } = configData!;
    const findUrlRes = consumerApiUrls.find((item) => item.url === url);
    if (findUrlRes) {
      const priceList: PriceConfigResDto[] = Local.get('price_list');
      const res = priceList.find((item) => item.dictKey === findUrlRes.dictKey);
      const params: CreatePayReqDto = {
        payAmount: res!.price!.toString(),
        payType: 3,
        businessType: findUrlRes.businessType ?? 2,
        businessParam: findUrlRes.dictKey,
        businessParamName: res?.productName,
      };
      const payRes = await createPay(params);
      if (payRes) {
        configData!.headers.orderNo = payRes.orderNo;
      } else {
        // 取消请求
        requestManager.cancelRequest(requestId, '支付失败');
        throw new Error('支付失败');
      }
    }
    if (configData!.otherApi) {
      configData!.baseURL = configData!.otherApi;
    }
    // 数据序列化处理
    if (
      configData!.data &&
      typeof configData!.data === 'object' &&
      !(configData!.data instanceof FormData) &&
      (configData!.headers?.['Content-Type'] as string)?.includes('application/json')
    ) {
      configData!.data = JSON.stringify(configData!.data);
    }
    return configData;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);
let isLogout = false;

function isAuthApi(url?: string): boolean {
  if (!url) return false;
  return /\/api\/auth\//.test(url);
}

function isPermissionDeniedResponse(data: ErrorResponse['data'], status?: number): boolean {
  const messageText = String(data?.message || data?.error || '').trim();
  return status === 403
    || data?.code === 403
    || /仅.*可访问|权限不足|无权访问|禁止访问/.test(messageText);
}

function triggerLogout(messageText?: string): never {
  if (!isLogout) {
    isLogout = true;
    if (messageText) {
      messageApi.error(messageText);
    }
    logoutAndRedirect();
  }
  throw new Error(messageText || '登录已失效');
}

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse<BaseResponse>) => {
    const { data, config, status } = response
    const requestConfig = config as CustomAxiosRequestConfig
    // 请求完成后移除控制器
    if (requestConfig?.requestId) {
      requestManager.removeController(requestConfig.requestId)
    }

    // 处理代理错误（返回了 HTML 而不是 JSON）
    const isProxyError =
      config.url?.includes('/v1/') &&
      typeof data === 'string' &&
      (data as string).startsWith('<!DOCTYPE')
    if (isProxyError) {
      return Promise.reject(new Error('404'))
    }
    // 处理 blob 类型数据，直接返回原始的 Blob 数据
    if (data instanceof Blob) {
      return data
    }
    // 源数据直接返回，不进行 data.data 处理
    if (
      (requestConfig?.sourceData && data.code === 200) ||
      (requestConfig?.sourceData && data.code === 400 && !data.data)
    ) {
      return data
    }
    // 业务错误处理
    if (data.success != null && !data.success) {
      const errorMessage = data?.msg || 'Error'
      // 业务错误处理逻辑，根据不同的 code 执行不同的操作
      if (REQUEST_CODE_HANDLE[data.code]) {
        REQUEST_CODE_HANDLE[data.code]?.(data as ApiErrorResponse)
        return Promise.reject(new Error(errorMessage))
      }
      // 兼容上传接口正确上传success 会返回 false
      if (compatibleData(config.url!, data.data)) {
        return data.data
      }
      // token 失效
      if (data?.code === 400 && data?.data === 401 && isAuthApi(config.url)) {
        return triggerLogout(errorMessage)
      }
      // 记录业务错误埋点
      if (trackingSDK.isInit()) {
        trackingSDK.trackApiError(
          config.url || '',
          config.method?.toUpperCase() || 'GET',
          status,
          errorMessage,
          data,
          config.data,
        )
      }
      messageApi.error(errorMessage)
      return Promise.reject(new Error(errorMessage))
    }
    if (data.code === 500 || data.code === 400) {
      return Promise.reject(new Error(data.message));
    }

    if (!requestConfig.silent && data?.msg) {
      messageApi.success(data?.msg);
    }

    return data.data;
  },
  (error: AxiosError<ErrorResponse>) => {
    // 这里处理 HTTP 状态码为 4xx、5xx 的响应
    // 或者网络错误、请求超时等
    const config = error.config as CustomAxiosRequestConfig | undefined;
    // 请求完成后移除控制器
    if (config?.requestId) {
      requestManager.removeController(config.requestId);
    }

    // 如果是取消请求的错误，不进行后续处理
    if (axios.isCancel(error)) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        isCanceled: true,
        message: '请求已取消',
        requestId: config?.requestId,
      });
    }

    const { status } = error.response || {};
    const data = error.response?.data || {};
    if (
      !isPermissionDeniedResponse(data, status)
      && (data.code === 401 || status === 401 && isAuthApi(config?.url))
    ) {
      return triggerLogout(data?.message || data?.error || '登录已失效，请重新登录');
    }
    // 记录接口错误埋点
    if (trackingSDK.isInit()) {
      const errorMessage = data?.error || '请求错误';
      const responseData = data;
      const params = config?.data;
      trackingSDK.trackApiError(
        config?.url || '',
        config?.method?.toUpperCase() || 'GET',
        error.status || status || 0,
        errorMessage,
        responseData,
        params,
      );
    }
    return Promise.reject(error);
  },
);

// 请求方法类型定义
interface RequestMethods {
  <T = any>(config: RequestConfig): Promise<T>;
  <T = any>(url: string, config?: RequestConfig): Promise<T>;
  get: <T = any>(url: string, config?: RequestConfig) => Promise<T>;
  delete: <T = any>(url: string, config?: RequestConfig) => Promise<T>;
  post: <T = any>(url: string, data?: any, config?: RequestConfig) => Promise<T>;
  put: <T = any>(url: string, data?: any, config?: RequestConfig) => Promise<T>;
  patch: <T = any>(url: string, data?: any, config?: RequestConfig) => Promise<T>;
  head: <T = any>(url: string, config?: RequestConfig) => Promise<T>;
  options: <T = any>(url: string, config?: RequestConfig) => Promise<T>;
  cancelRequest: (requestId: string) => boolean;
  cancelAllRequests: () => void;
}

// 创建强类型的请求实例
let request = service as typeof service & RequestMethods;

// 为请求方法添加类型重载
// 1. 只传配置对象
request = async function <T = any>(config: RequestConfig): Promise<T> {
  const response = await service(config);
  return response as T;
} as typeof request;

// 2. 传 URL 和配置
request = async function <T = any>(url: string, config?: RequestConfig): Promise<T> {
  const response = await service({ ...config, url });
  return response as T;
} as typeof request;

// 3. 各个 HTTP 方法
request.get = async function <T = any>(url: string, config?: RequestConfig): Promise<T> {
  const response = await service.get(url, config);
  return response as T;
};

request.delete = async function <T = any>(url: string, config?: RequestConfig): Promise<T> {
  const response = await service.delete(url, config);
  return response as T;
};

request.post = async function <T = any>(
  url: string,
  data?: any,
  config?: RequestConfig,
): Promise<T> {
  const response = await service.post(url, data, config);
  return response as T;
};

request.put = async function <T = any>(
  url: string,
  data?: any,
  config?: RequestConfig,
): Promise<T> {
  const response = await service.put(url, data, config);
  return response as T;
};

request.patch = async function <T = any>(
  url: string,
  data?: any,
  config?: RequestConfig,
): Promise<T> {
  const response = await service.patch(url, data, config);
  return response as T;
};

request.head = async function <T = any>(url: string, config?: RequestConfig): Promise<T> {
  const response = await service.head(url, config);
  return response as T;
};

request.options = async function <T = any>(url: string, config?: RequestConfig): Promise<T> {
  const response = await service.options(url, config);
  return response as T;
};

// 4. 添加取消请求的方法
request.cancelRequest = function (requestId: string): boolean {
  return requestManager.cancelRequest(requestId);
};

request.cancelAllRequests = function (): void {
  requestManager.cancelAllRequests();
};

export { baseURL, request as default, glodUrl, request };

// 使用示例类型
export interface User {
  id: number;
  name: string;
  email: string;
}

export interface ApiResponse<T = any> {
  code: number;
  data: T;
  timestamp: number;
  msg: string;
}

/**
 * 消费参数
 */
interface CreatePayReqDto {
  /**
   * 业务参数
   * <p>
   * 1. 单次查：查询的菜单key
   * 2. 自定义查：查询的菜单key（多个","）隔开
   * </p>
   * 业务参数
   */
  businessParam: string;
  /**
   * 业务类型（1=单次查，2=自定义查）
   */
  businessType: number;
  /**
   * 支付金额
   */
  payAmount: number | string;
  /**
   * 支付方式（1=支付宝，2=微信，3=金币）
   */
  payType: number;
  /**
   * 业务参数名称
   */
  businessParamName?: string;
  [property: string]: any;
}

/**
 * 消费参数返回内容
 */
interface CreatePayResDto {
  /**
   * 订单id
   */
  id?: number;
  /**
   * 订单编号
   */
  orderNo?: string;
  /**
   * 支付方式（1=支付宝，2=微信，3=金币）
   */
  payType?: number;
  /**
   * 状态（0=待支付，1=已支付，-1=已退款，-2=已取消）
   */
  status?: number;
  [property: string]: any;
}

/**
 * 创建消费
 * @param params
 * @returns
 */
export const createPay = async (params: CreatePayReqDto) => {
  const requestId = requestManager.generateRequestId('createPay');

  try {
    const data = await request.post<ApiResponse<CreatePayResDto>>('Search.createPay', params, {
      silent: true,
      sourceData: true,
      requestId, // 传递请求ID
      otherApi: import.meta.env.VITE_API_URL,
      headers: {
        Authorization: Local.get('capitalToken') || '',
      }
    });
    return data.data;
  } catch (error: any) {
    // 如果是取消请求，不记录错误
    if (!error.isCanceled) {
      console.error('创建消费错误', error);
    }
  }
  return '';
};

// 使用示例：
/*
import request, { requestManager } from './request'

// 发起请求时可以指定请求ID
const user = await request.get<User>('/api/user/1', {
  requestId: 'getUser'
})

// 取消特定请求
request.cancelRequest('getUser')

// 取消所有请求
request.cancelAllRequests()
*/
